/**
 * İade toplamı, tahsilat / kapora / ek ödeme gibi gerçek alınan türlerinin toplamını geçemez.
 * Tür adları SABIT ST_TUR=128 ST_AD üzerinden (tr-TR küçük harf) eşleşir.
 */

export function turAdLowerTr(stAd) {
    return String(stAd || '').toLocaleLowerCase('tr-TR');
}

/** Para satırı iade mi (tür adında "iade") */
export function isIadeTurAd(stAd) {
    return turAdLowerTr(stAd).includes('iade');
}

/**
 * Tahsilat, kapora, ek ödeme vb. — borç/anlaşma ve iade hariç, alınan (ödeme girişi) türleri.
 * CP_BA=false ile birlikte kullanılmalı.
 */
export function isOdemeKaynakAlinanTurAd(stAd) {
    const l = turAdLowerTr(stAd);
    if (!l.trim()) return false;
    if (l.includes('anlaş') || l.includes('anlas')) return false;
    if (l.includes('iade')) return false;
    return (
        l.includes('tahsilat')
        || l.includes('kapora')
        || l.includes('kaparo')
        || l.includes('ekodeme')
        || l.includes('ek ödeme')
        || (l.includes('ek') && (l.includes('ödeme') || l.includes('odeme')))
    );
}

export function rowIsAlinanBa(row) {
    return row?.CP_BA === false || row?.CP_BA === 0 || row?.CP_BA === '0';
}

/**
 * @param {Array} list - CARI_PARA satırları
 * @param {function(number): string} getTurAd - CP_TUR -> ST_AD
 */
export function computeIadeVsOdemeKaynakTotals(list, getTurAd) {
    const rows = Array.isArray(list) ? list : [];
    let iadeToplam = 0;
    let odemeAlinanToplam = 0;
    for (const r of rows) {
        const ad = getTurAd(Number(r?.CP_TUR));
        const tut = Number(r?.CP_TUTAR) || 0;
        if (isIadeTurAd(ad)) iadeToplam += tut;
        if (rowIsAlinanBa(r) && isOdemeKaynakAlinanTurAd(ad)) odemeAlinanToplam += tut;
    }
    return { iadeToplam, odemeAlinanToplam };
}

const EPS = 0.005;

function formatTryTr(n) {
    return (Number(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * İade > tahsilat-kaynak alınan toplamı ise kullanıcıya gösterilecek mesaj; aksi halde null.
 */
export function getIadeOdemeKaynakBlockMessage(list, getTurAd) {
    const { iadeToplam, odemeAlinanToplam } = computeIadeVsOdemeKaynakTotals(list, getTurAd);
    if (iadeToplam <= odemeAlinanToplam + EPS) return null;
    const eksik = Math.max(0, iadeToplam - odemeAlinanToplam);
    return (
        `Kayıtlı iadelerin toplamı ${formatTryTr(iadeToplam)} TL; tahsilat toplamı ${formatTryTr(odemeAlinanToplam)} TL. ` +
        `tahsilat toplamı iade toplamından daha küçük olamaz.`
    );
}

export function isIadeOverOdemeKaynakLimit(list, getTurAd) {
    return getIadeOdemeKaynakBlockMessage(list, getTurAd) != null;
}

/**
 * Satış gridi / lst ile aynı: iade + min(iade, tahsilat-kaynak alınan) nötrlenir.
 * ALACAK = borç (iade türü hariç); TOPLAM_ALINAN = tüm alınan − nötr; KALAN = ALACAK − TOPLAM_ALINAN.
 */
export function computeParaSummaryForSatisGrid(list, getTurAd) {
    const rows = Array.isArray(list) ? list : [];
    let borcNonIade = 0;
    let iadeToplam = 0;
    let toplamAlinan = 0;
    let tdepSum = 0;
    for (const r of rows) {
        const ad = getTurAd(Number(r?.CP_TUR));
        const tut = Number(r?.CP_TUTAR) || 0;
        const isBorc = r?.CP_BA === true || r?.CP_BA === 1 || r?.CP_BA === '1';
        const isAlinan = r?.CP_BA === false || r?.CP_BA === 0 || r?.CP_BA === '0';
        if (isIadeTurAd(ad)) {
            iadeToplam += tut;
        } else if (isBorc) {
            borcNonIade += tut;
        }
        if (isAlinan) {
            toplamAlinan += tut;
            if (isOdemeKaynakAlinanTurAd(ad)) tdepSum += tut;
        }
    }
    const neutral = Math.min(iadeToplam, tdepSum);
    const ALACAK = borcNonIade;
    const TOPLAM_ALINAN = toplamAlinan - neutral;
    const KALAN = ALACAK - TOPLAM_ALINAN;
    return { ALACAK, TOPLAM_ALINAN, KALAN };
}
